import { useState, useEffect, useCallback } from "react";
import Login from "./pages/Login.jsx";
import MainPage from "./pages/MainPage.jsx";
import DetailPage from "./pages/DetailPage.jsx";
import { api } from "./lib/api.js";
import { useFontScale } from "./lib/useFontScale.js";

/**
 * Root.
 *
 * Auth states:
 *   loading  → whoami still in flight (dark blank so nothing flashes)
 *   !user    → Login screen
 *   user     → MainPage OR DetailPage, driven by the URL:
 *                /            → MainPage (watchlist)
 *                /stock/NVDA  → DetailPage for NVDA
 *
 * The URL is the source of truth for which view + which stock is showing,
 * so a full page reload (or a shared link) reproduces the exact view.
 * `_redirects` in web/public serves index.html for all non-file paths so
 * /stock/X doesn't 404 on refresh.
 */

// Parse the current location into a route.
// Returns `{ symbol }` for /stock/X, `{}` for /.
const STOCK_ROUTE_RE = /^\/stock\/([A-Za-z0-9.\-]+)\/?$/;
function parseLocation() {
  const m = window.location.pathname.match(STOCK_ROUTE_RE);
  return m ? { symbol: m[1].toUpperCase() } : {};
}

export default function App() {
  const [status, setStatus] = useState("loading"); // 'loading' | 'anon' | 'auth'
  const [user, setUser] = useState(null);

  // `selected` mirrors the URL. Initial value read synchronously from
  // window.location so a hard reload of /stock/NVDA renders DetailPage
  // on the very first paint — no MainPage flash in between.
  //
  // If we land directly on /stock/X (refresh, shared link, direct entry),
  // we seed a "/" entry UNDERNEATH the current one in the browser history
  // stack. That way pressing Chrome's back button (or the phone's back
  // gesture) always returns to the StockTweaks watchlist, never pops the
  // user out of the site to whatever they were viewing before.
  //
  // history.replaceState("/") rewrites the current entry to "/", then
  // pushState("/stock/X") puts /stock/X on top of it. Net history:
  //   [ … prior entries …,  "/",  "/stock/X"  ]  (current)
  // Browser back → pops "/stock/X" → lands on "/" → popstate fires →
  // parseLocation() returns {} → selected=null → MainPage renders.
  const [selected, setSelected] = useState(() => {
    const parsed = parseLocation();
    if (parsed.symbol) {
      // Seed the underlying "/" entry so back never exits the app.
      // Runs synchronously during the initial useState — the URL bar
      // never flashes "/" because no paint happens between these two
      // history calls.
      try {
        window.history.replaceState({}, "", "/");
        window.history.pushState({}, "", `/stock/${parsed.symbol}`);
      } catch { /* Safari private-mode etc. — non-fatal, back button
                   just falls back to the prior default behavior. */ }
    }
    return parsed.symbol || null;
  });

  // Fluid font scaler (see lib/useFontScale.js).
  useFontScale();

  // Keep `selected` in sync with browser back/forward.
  useEffect(() => {
    const onPop = () => setSelected(parseLocation().symbol || null);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // Navigate to a stock's detail page — pushes /stock/X and updates state.
  // Use useCallback so identity is stable across renders (MainPage's
  // memoized bits don't need to re-render just because we render).
  const openStock = useCallback((sym) => {
    if (!sym) return;
    const upper = String(sym).toUpperCase();
    const target = `/stock/${upper}`;
    if (window.location.pathname !== target) {
      window.history.pushState({}, "", target);
    }
    setSelected(upper);
    // Scroll to top so the detail view starts at the header, not
    // wherever the watchlist was scrolled to.
    window.scrollTo(0, 0);
  }, []);

  // Back to the watchlist. Because every detail-view mount seeds a "/"
  // entry underneath itself in the history stack, history.back() is
  // always safe — it lands on "/" and popstate re-derives state.
  const backToList = useCallback(() => {
    window.history.back();
  }, []);

  const loadWhoami = () =>
    api.auth.whoami()
      .then(res => {
        if (res.user) { setUser(res.user); setStatus("auth"); }
        else          { setUser(null);      setStatus("anon"); }
      })
      .catch(() => { setUser(null); setStatus("anon"); });

  useEffect(() => { loadWhoami(); }, []);

  const onLogout = async () => {
    try { await api.auth.logout(); } catch {}
    setUser(null);
    setSelected(null);
    // Reset URL to root on logout so a subsequent login lands on MainPage.
    if (window.location.pathname !== "/") {
      window.history.replaceState({}, "", "/");
    }
    setStatus("anon");
  };

  if (status === "loading") {
    return <div style={{ minHeight: "100vh", background: "#0A1428" }} />;
  }
  if (status === "anon" || !user) {
    return <Login />;
  }
  return selected
    ? <DetailPage symbol={selected} onBack={backToList} />
    : <MainPage onOpen={openStock} user={user} onLogout={onLogout} />;
}
